//@flow
import React, { PureComponent } from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import compose from 'recompose/compose';

import TeamActions from 'common/redux/actions/teamMaster';
import ToastActions from 'common/redux/actions/toast';
import { dashboardColors } from 'common/enums/ColorEnum';
import AppPreferencesActions from 'common/redux/actions/appPreferences';

import {
    clientSelector,
    isAddEntityActive as isAddEntityActiveSelector,
    isImportEntityActive as isImportEntityActiveSelector,
    isExportEntityActive as isExportEntityActiveSelector,
    editEntityIdSelector
} from 'common/redux/selectors/queryParamsSelectors';
import { getEntitySettings } from 'common/redux/selectors/clientSelector';
import { getEntityTablePreferences } from 'common/redux/selectors/appPreferences';
import TeamMasterSelectors from 'common/redux/selectors/teamMaster';
import { isGlobalPersonnelSelector } from 'common/redux/selectors/sessionSelector';
import { RoleOperationEnum } from 'common/modules/rbac';

import { Checkbox } from 'common/components/core/checkbox';
import MasterExportContainer from 'common/entity/generic/MasterExportContainer';
import { withRouter } from 'common/components/core/router';
import theme from 'common/entity/BulkEditContainer.scss';
import teamMasterTheme from 'common/entity/teamMaster/TeamMasterContainer.scss';

import {
    type EntityColumnType,
    entityMasterFactory
} from 'common/entity/generic/MasterContainer';
import MasterContainerHOC from 'common/entity/generic/MasterContainerHOC';
import SingleTeamEditContainer from 'common/entity/teamMaster/SingleTeamEditContainer';
import TeamMasterImportContainer from 'common/entity/teamMaster/TeamMasterImportContainer';
import VisitUtils from 'common/utils/VisitUtils';
import {
    CellDataTypes,
    FROZEN_COLUMN_LIMIT
} from 'common/components/core/table';
import EnableTeamModal from 'common/entity/teamMaster/EnableTeamModal';
import { fetchTeamsByClient } from 'common/redux/actions/team';

import type { TFunction } from 'react-i18next';
import type { EntitySettings } from 'common/models/internal/EntitySettings';

import {
    ENTITY_NAME_ID,
    ENTITY_EXPORT_ID,
    ENTITY_TABLE_ID
} from 'common/constants/entity/teamMaster';
import PlatformEntityExportHandler from 'common/entity/common/platform/PlatformEntityExportHandler';

const DEFAULT_COLUMNS: Array<EntityColumnType> = [
    {
        id: 'selector',
        Cell: props => {
            const {
                tdProps: {
                    rest: { isChecked, onCheckboxClick }
                }
            } = props;
            return (
                <Checkbox
                    checkboxColor={dashboardColors.darkSkyBlue.name}
                    className={theme.selectionCell}
                    theme={theme}
                    checked={isChecked}
                    onChange={onCheckboxClick}
                />
            );
        },
        sortable: false,
        filterable: false,
        frozen: true,
        disablePinControl: true,
        width: 40
    },
    {
        headerTextId: 'teamMasterTable.id',
        accessor: 'id',
        minWidth: 100,
        infixSearchable: true,
        frozen: true
    },
    {
        headerTextId: 'teamMasterTable.code',
        accessor: 'code',
        frozen: true,
        infixSearchable: true,
        show: false
    },
    {
        headerTextId: 'teamMasterTable.name',
        accessor: 'name',
        Cell: props => {
            const {
                original: { name, color }
            } = props;
            const style = { borderLeft: `5px solid ${color}` };
            return (
                <div
                    className={classnames(
                        theme.cellText,
                        teamMasterTheme.teamName
                    )}
                    style={style}
                >
                    {name}
                </div>
            );
        }
    },
    {
        headerTextId: 'teamMasterTable.shortCode',
        accessor: 'shortCode',
        exportId: 'shortCode',
        sortable: true,
        filterable: true
    },
    {
        headerTextId: 'teamMasterTable.businessUnitId',
        accessor: 'businessUnitId.businessUnitId',
        exportId: 'businessUnitId',
        sortable: false,
        filterable: false
    },
    {
        headerTextId: 'teamMasterTable.cityId',
        accessor: 'cityId.cityId',
        exportId: 'cityId',
        sortable: true,
        filterable: true
    },
    {
        headerTextId: 'teamMasterTable.updatedBy',
        id: 'updatedBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.updatedBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const updatedBy = VisitUtils.formatIDs(
                original?.auditMetadata?.updatedBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={updatedBy} className={theme.cellText}>
                    {updatedBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'teamMasterTable.updatedOn',
        accessor: 'auditMetadata.updatedOn',
        id: 'updatedOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    },
    {
        headerTextId: 'teamMasterTable.createdBy',
        id: 'createdBy',
        sortable: false,
        filterable: false,
        fieldId: 'auditMetadata.createdBy',
        Cell: props => {
            const {
                original,
                tdProps: {
                    rest: { isGlobalPersonnel }
                }
            } = props;
            const createdBy = VisitUtils.formatIDs(
                original?.auditMetadata?.createdBy?.id,
                !isGlobalPersonnel
            );
            return (
                <span title={createdBy} className={theme.cellText}>
                    {createdBy}
                </span>
            );
        }
    },
    {
        headerTextId: 'teamMasterTable.createdOn',
        accessor: 'auditMetadata.createdOn',
        id: 'createdOn',
        sortable: false,
        filterable: false,
        dataType: CellDataTypes.DateTime
    }
];

type MasterContainerType = {
    EntityEditViewContainer: Node,
    EntityImportContainer: Node,
    COLUMNS: Array<EntityColumnType>,
    options: {
        entityTableId: string
    },
    ActionsBar: Node,
    BulkViewEmptyState: string,
    fetchEntities: Function,
    EnableEntityView: Node
};

type Props = {
    clientId: string,
    fetchTeamsByClient: ({ clientId: string }) => void,
    t: TFunction,
    entitySettings: EntitySettings,
    selectedRows: Array<string>,
    toggleRowSelection: string => void,
    theme: Object,
    isGlobalPersonnel: boolean,
    fetchOptions: Object
};

const teamMasterContainerFactory = ({
    EntityEditViewContainer = SingleTeamEditContainer,
    EntityImportContainer = TeamMasterImportContainer,
    COLUMNS = DEFAULT_COLUMNS,
    options = {
        entityTableId: ENTITY_TABLE_ID,
        operations: {
            add: RoleOperationEnum.UPDATE_TEAMS,
            edit: RoleOperationEnum.UPDATE_TEAMS,
            export: RoleOperationEnum.EXPORT_TEAMS
        }
    },
    ActionsBar,
    BulkViewEmptyState,
    fetchEntities = TeamActions.fetchActiveEntities,
    EnableEntityView = EnableTeamModal
}: MasterContainerType) => {
    const TeamMaster = entityMasterFactory({
        EntityEditViewContainer,
        EntityImportContainer,
        EntityExportContainer: MasterExportContainer,
        COLUMNS,
        EXPORT_HANDLER: new PlatformEntityExportHandler(),
        options,
        ActionsBar,
        BulkViewEmptyState,
        EnableEntityView
    });
    class TeamMasterContainer extends PureComponent<Props> {
        componentWillUnmount() {
            //Update the teams list when leaving team master
            const { clientId, fetchTeamsByClient } = this.props;
            fetchTeamsByClient({ clientId });
        }

        getTdProps = (state, rowInfo) => {
            if (!rowInfo) {
                return {};
            }
            const {
                original: { id }
            } = rowInfo;
            const {
                t,
                entitySettings,
                selectedRows,
                toggleRowSelection,
                theme,
                isGlobalPersonnel
            } = this.props;
            const isChecked = selectedRows.indexOf(id) >= 0;
            return {
                onCheckboxClick: () => {
                    toggleRowSelection(id);
                },
                t,
                entitySettings,
                theme,
                isChecked,
                isGlobalPersonnel,
                className: classnames({ [theme.selectedRow]: isChecked })
            };
        };

        render() {
            const { clientId, fetchOptions, t } = this.props;
            const teamMasterFetchOptions = {
                clientId,
                ...(fetchOptions || {})
            };
            return (
                <TeamMaster
                    {...this.props}
                    emptyStateImageSrc="/img/masterTeam-empty.svg"
                    entityNameId={ENTITY_NAME_ID}
                    entityExportId={ENTITY_EXPORT_ID}
                    entityId="teamId"
                    entitySettingsName="teamSettings"
                    fetchOptions={teamMasterFetchOptions}
                    getTdProps={this.getTdProps}
                    hasFreezableColumns
                    frozenColumnLimit={FROZEN_COLUMN_LIMIT}
                    pinTooltip={t('pinTooltip')}
                    unpinTooltip={t('unpinTooltip')}
                    entityCanBeDisabled
                />
            );
        }
    }

    const mapStateToProps = (state, ownProps) => {
        const clientId = clientSelector(state, ownProps);
        const isLoading = TeamMasterSelectors.isEntitiesLoading(
            state,
            ownProps
        );
        const data = TeamMasterSelectors.getEntities(state, ownProps);
        const isAddEntityActive = isAddEntityActiveSelector(state, ownProps);
        const entityIdToEdit = editEntityIdSelector(state, ownProps);
        const entitySettings = getEntitySettings(state, ownProps);
        const tableSettings = getEntityTablePreferences(state, ENTITY_TABLE_ID);
        const isImportEntityActive = isImportEntityActiveSelector(
            state,
            ownProps
        );
        const isExportEntityActive = isExportEntityActiveSelector(
            state,
            ownProps
        );
        const isGlobalPersonnel = isGlobalPersonnelSelector(state, ownProps);

        return {
            clientId,
            isLoading,
            data,
            isAddEntityActive,
            entityIdToEdit,
            entitySettings,
            tableSettings,
            isImportEntityActive,
            isExportEntityActive,
            isGlobalPersonnel
        };
    };

    const withRedux = connect(mapStateToProps, {
        fetchEntities,
        fetchTeamsByClient,
        showToastAction: ToastActions.showToast,
        updateToastAction: ToastActions.updateToast,
        hideToastAction: ToastActions.hideToast,
        disableEntity: TeamActions.disableEntity,
        clearData: TeamActions.clearData,
        saveTableSettings: AppPreferencesActions.saveEntityTablePreferences,
        bulkDisableEntity: TeamActions.bulkDisableEntity
    });

    const enhance = compose(
        themr(null, theme),
        withRouter,
        withRedux,
        translate('entityManagement'),
        MasterContainerHOC
    );
    return enhance(TeamMasterContainer);
};

const ThemedConnectedTeamMasterContainer = teamMasterContainerFactory({});

export {
    teamMasterContainerFactory,
    ThemedConnectedTeamMasterContainer as TeamMasterContainer
};
export default ThemedConnectedTeamMasterContainer;
